import { FC, KeyboardEvent, ReactNode, RefObject } from 'react'
import { AutoComplete } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import LegacySearchIcon from '@rsuite/icons/legacy/Search'
import { PickerInstance } from 'rsuite/esm/Picker'

import useIsOnMobile from '../../../../services/useIsOnMobile'
import { SearchMenuItemLabel } from './SearchMenuItem'
import { DropdownMenuData } from './SearchDropdownMenu'

import styles from '../../../../styles/Search.module.less'

interface SearchInputProps {
  searchTerm: string
  searchMenuData: DropdownMenuData[]
  isLargeInput: boolean
  collapsed: boolean
  containerRef: RefObject<HTMLDivElement>
  searchInputRef: RefObject<PickerInstance>
  onChange: (val: string) => void
  onKeyUp: (event: KeyboardEvent<Element>) => void
  onClearClick: () => void
  onSearchClick: () => void
  renderMenu: () => ReactNode
}

const SearchInput: FC<SearchInputProps> = ({
  searchTerm,
  searchMenuData,
  isLargeInput,
  collapsed,
  containerRef,
  searchInputRef,
  onChange,
  onKeyUp,
  onClearClick,
  onSearchClick,
  renderMenu,
}) => {
  const { t } = useTranslation('common')
  const isOnMobile = useIsOnMobile()

  return (
    <div className={`${styles['search-input-container']} ${isLargeInput ? styles.large : ''} ${collapsed ? styles.collapsed : ''}`}>
      <AutoComplete
        ref={searchInputRef}
        data={searchMenuData}
        open={isLargeInput || collapsed ? false : undefined}
        filterBy={() => true} // Always show all data that is passed
        onChange={onChange}
        onKeyUp={onKeyUp}
        container={containerRef.current || undefined}
        className="input-round"
        menuClassName={styles['auto-complete-menu']}
        placement="bottomStart"
        data-testid="search-input"
        placeholder={isOnMobile ? t('Search for product') : t('Search for product, category or supplier')}
        value={searchTerm}
        renderMenuItem={(label: ReactNode) => (
          <SearchMenuItemLabel label={label} />
        )}
        renderMenu={renderMenu}
      />
      {searchTerm && (
        // Copied markup from rs-picker, inherits styles from global scope
        <button
          className="rs-picker-toggle-clean"
          type="button"
          tabIndex={-1}
          onClick={onClearClick}
          data-testid="clear-btn"
        >
          ✕
        </button>
      )}
      {!isLargeInput ? (
        <LegacySearchIcon
          className={styles['search-icon']}
          role="button"
          data-testid="search-icon"
          onClick={onSearchClick}
        />
      ) : null}
    </div>
  )
}

export default SearchInput
